import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  X,
  AlertCircle,
  FileCheck,
  Loader2,
  MessageSquare,
  Calendar,
  User,
  ChevronRight,
  ChevronLeft,
  Download,
  Filter,
  SortAsc,
  Hash,
  ExternalLink,
  TrendingUp,
  BarChart3
} from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import Layout from "../../../components/web/Layout";
import { Helmet } from "react-helmet";
import axios from "axios";

const StatusBadge = ({ status, size = "md" }) => {
  const statusConfig = {
    'pending': {
      icon: <Clock className={`${size === "sm" ? "w-3 h-3" : "w-4 h-4"}`} />,
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      label: "Pending",
      gradient: "from-amber-400 to-amber-500"
    },
    'approved': {
      icon: <CheckCircle className={`${size === "sm" ? "w-3 h-3" : "w-4 h-4"}`} />,
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      border: "border-emerald-200",
      label: "Approved",
      gradient: "from-emerald-400 to-emerald-500"
    },
    'under_review': {
      icon: <Loader2 className={`${size === "sm" ? "w-3 h-3" : "w-4 h-4"}`} />,
      bg: "bg-blue-50",
      text: "text-blue-700",
      border: "border-blue-200",
      label: "Under Review",
      gradient: "from-blue-400 to-blue-500"
    },
    'rejected': {
      icon: <XCircle className={`${size === "sm" ? "w-3 h-3" : "w-4 h-4"}`} />,
      bg: "bg-rose-50",
      text: "text-rose-700",
      border: "border-rose-200",
      label: "Rejected",
      gradient: "from-rose-400 to-rose-500"
    }
  };

  const statusKey = status?.toLowerCase().replace(' ', '_') || 'pending';
  const config = statusConfig[statusKey] || statusConfig.pending;
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${config.bg} ${config.text} ${config.border} text-sm font-medium transition-all hover:scale-105`}>
      <div className={`p-1 rounded-full bg-gradient-to-r ${config.gradient} bg-opacity-10`}>
        {config.icon}
      </div>
      <span>{status || "Pending"}</span>
    </div>
  );
};

const DocumentStatusBadge = ({ status, size = "md" }) => {
  const statusConfig = {
    'received': {
      icon: <FileText className={`${size === "sm" ? "w-3 h-3" : "w-4 h-4"}`} />,
      bg: "bg-slate-50",
      text: "text-slate-700",
      border: "border-slate-200",
      label: "Received",
      gradient: "from-slate-400 to-slate-500"
    },
    'under_review': {
      icon: <Eye className={`${size === "sm" ? "w-3 h-3" : "w-4 h-4"}`} />,
      bg: "bg-indigo-50",
      text: "text-indigo-700",
      border: "border-indigo-200",
      label: "Under Review",
      gradient: "from-indigo-400 to-indigo-500"
    },
    'incomplete_documents': {
      icon: <AlertCircle className={`${size === "sm" ? "w-3 h-3" : "w-4 h-4"}`} />,
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      label: "Incomplete",
      gradient: "from-amber-400 to-amber-500"
    },
    'verified': {
      icon: <FileCheck className={`${size === "sm" ? "w-3 h-3" : "w-4 h-4"}`} />,
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      border: "border-emerald-200",
      label: "Verified",
      gradient: "from-emerald-400 to-emerald-500"
    }
  };

  const statusKey = status?.toLowerCase().replace(' ', '_') || 'received';
  const config = statusConfig[statusKey] || statusConfig.received;
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${config.bg} ${config.text} ${config.border} text-sm font-medium transition-all hover:scale-105`}>
      <div className={`p-1 rounded-full bg-gradient-to-r ${config.gradient} bg-opacity-10`}>
        {config.icon}
      </div>
      <span>{status || "Received"}</span>
    </div>
  );
};

const ApplicationTrack = () => {
  const [memberNo, setMemberNo] = useState("");
  const [forms, setForms] = useState([]);
  const [offset, setOffset] = useState(0);
  const limit = 8;
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [history, setHistory] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  useEffect(() => {
    AOS.init({ 
      duration: 800,
      once: true,
      offset: 50
    });
  }, []);

  const searchForms = async (newOffset = 0) => {
    if (!memberNo.trim()) {
      setSearchError("Please enter your membership number");
      return;
    }

    setLoading(true);
    setSearchError("");
    
    try {
      const res = await axios.get(
        `https://actadcd.in/backend/api/forms/search?member_no=${memberNo}&limit=${limit}&offset=${newOffset}`
      );

      if (res.data.data.length === 0) {
        setSearchError("No applications found for this membership number");
      }
      
      setForms(res.data.data);
      setTotal(res.data.total);
      setOffset(newOffset);
    } catch (error) {
      setSearchError("Failed to fetch applications. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openHistory = async (form) => {
    setSelectedForm(form);
    setHistoryLoading(true);
    
    try {
      const res = await axios.get(
        `https://actadcd.in/backend/api/forms/history?form_id=${form.form_id}&form_type=${form.form_type}`
      );
      setHistory(res.data.data);
      setShowModal(true);
    } catch (error) {
      setSearchError("Failed to load application history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchForms(0);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredForms = useMemo(() => {
    let filtered = [...forms];
    
    if (filterStatus !== "all") {
      filtered = filtered.filter(form => form.status?.toLowerCase() === filterStatus.toLowerCase());
    }
    
    if (sortBy === "newest") {
      filtered.sort((a, b) => new Date(b.submitted_date || b.created_at) - new Date(a.submitted_date || a.created_at));
    } else if (sortBy === "oldest") {
      filtered.sort((a, b) => new Date(a.submitted_date || a.created_at) - new Date(b.submitted_date || b.created_at));
    }
    
    return filtered;
  }, [forms, filterStatus, sortBy]);

  const getStatusStats = () => {
    const stats = {
      pending: 0,
      approved: 0,
      under_review: 0,
      rejected: 0
    };
    
    forms.forEach(form => {
      const status = form.status?.toLowerCase().replace(' ', '_') || 'pending';
      if (stats[status] !== undefined) {
        stats[status]++;
      }
    });
    
    return stats;
  };

  const statusStats = getStatusStats();

  return (
    <Layout>
      <Helmet>
        <title>Application Tracking Dashboard | ACTA DCD</title>
        <meta name="description" content="Track your application status and document verification progress" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 py-8 md:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 shadow-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                Application Tracker
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Track Your Applications
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Monitor your application status and document verification progress in real-time
            </p>
          </div>

          {/* Search Section */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-10 border border-gray-100">
            <div className="max-w-4xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Search className="w-6 h-6 text-blue-500" />
                    Find Your Applications
                  </h2>
                  <div className="hidden md:flex items-center gap-2 text-sm text-gray-500">
                    <Hash className="w-4 h-4" />
                    <span>Enter membership number</span>
                  </div>
                </div>
                <p className="text-gray-600">
                  Enter your membership number to view all submitted applications and their current status
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={memberNo}
                    onChange={(e) => {
                      setMemberNo(e.target.value.toUpperCase());
                      setSearchError("");
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter Membership Number (e.g., ACTA123456)"
                    className="w-full border-2 border-gray-200 rounded-xl px-6 py-4 text-gray-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all text-lg placeholder-gray-400"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="p-2 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
                      <Hash className="w-5 h-5 text-blue-500" />
                    </div>
                  </div>
                  {searchError && (
                    <p className="text-sm text-rose-600 mt-2 flex items-center gap-2 p-3 bg-rose-50 rounded-lg">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      {searchError}
                    </p>
                  )}
                </div>
                
                <button
                  onClick={() => searchForms(0)}
                  disabled={loading}
                  className="group relative bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3 min-w-[160px] text-lg overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-indigo-700 opacity-0 group-hover:opacity-100 transition-opacity" />
                  {loading ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin relative z-10" />
                      <span className="relative z-10">Searching...</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-6 h-6 relative z-10" />
                      <span className="relative z-10">Search</span>
                      <ChevronRight className="w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all relative z-10" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Stats Overview */}
          {forms.length > 0 && (
            <div className="mb-8" data-aos="fade-up">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                Application Overview
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {Object.entries(statusStats).map(([status, count]) => {
                  const config = {
                    pending: { label: "Pending", color: "bg-amber-500", text: "text-amber-700" },
                    approved: { label: "Approved", color: "bg-emerald-500", text: "text-emerald-700" },
                    under_review: { label: "Under Review", color: "bg-blue-500", text: "text-blue-700" },
                    rejected: { label: "Rejected", color: "bg-rose-500", text: "text-rose-700" }
                  }[status];
                  
                  return (
                    <div key={status} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{count}</p>
                          <p className={`text-sm font-medium ${config.text}`}>{config.label}</p>
                        </div>
                        <div className={`w-12 h-12 rounded-full ${config.color} bg-opacity-10 flex items-center justify-center`}>
                          <div className={`w-8 h-8 rounded-full ${config.color} flex items-center justify-center`}>
                            {status === 'pending' && <Clock className="w-4 h-4 text-white" />}
                            {status === 'approved' && <CheckCircle className="w-4 h-4 text-white" />}
                            {status === 'under_review' && <Loader2 className="w-4 h-4 text-white" />}
                            {status === 'rejected' && <XCircle className="w-4 h-4 text-white" />}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Results Section */}
          {forms.length > 0 && (
            <div className="mb-8" data-aos="fade-up">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Your Applications
                    <span className="ml-2 text-blue-600 bg-blue-50 px-3 py-1 rounded-full text-sm font-semibold">
                      {total} Total
                    </span>
                  </h3>
                  <p className="text-gray-500 text-sm mt-1">
                    Showing {offset + 1} - {Math.min(offset + limit, total)} applications
                  </p>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select 
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="under_review">Under Review</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <SortAsc className="w-4 h-4 text-gray-400" />
                    <select 
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid gap-4">
                {filteredForms.map((form, i) => (
                  <div
                    key={i}
                    data-aos="fade-up"
                    data-aos-delay={i * 50}
                    className="group bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden hover:-translate-y-1"
                  >
                    <div className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                        {/* Form Info */}
                        <div className="space-y-4">
                          <div className="flex items-start gap-4">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50">
                              <FileText className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-100 px-2 py-1 rounded">
                                {form.form_type}
                              </span>
                              <h4 className="font-bold text-gray-900 text-lg mt-2 flex items-center gap-2">
                                {form.form_number}
                                <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                              </h4>
                              
                              {form.submitted_date && (
                                <div className="flex items-center gap-2 mt-2">
                                  <Calendar className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm text-gray-600">
                                    Submitted: {formatShortDate(form.submitted_date)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap gap-3">
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-2">Application Status</p>
                              <StatusBadge status={form.status} />
                            </div>
                            {form.document_status && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-2">Document Status</p>
                                <DocumentStatusBadge status={form.document_status} />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Action Button */}
                        <button
                          onClick={() => openHistory(form)}
                          className="group relative bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 hover:text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:from-blue-600 hover:to-indigo-600 flex items-center gap-3 self-start lg:self-center border border-blue-100 hover:border-transparent"
                        >
                          <Eye className="w-5 h-5" />
                          <span>View Timeline</span>
                          <ChevronRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-8 border-t border-gray-200" data-aos="fade-up">
              <div className="text-sm text-gray-600">
                Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  disabled={offset === 0}
                  onClick={() => searchForms(offset - limit)}
                  className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, Math.ceil(total / limit)) }, (_, i) => {
                    const pageNum = i + 1;
                    const isCurrent = Math.floor(offset / limit) + 1 === pageNum;
                    return (
                      <button
                        key={i}
                        onClick={() => searchForms((pageNum - 1) * limit)}
                        className={`w-10 h-10 rounded-lg font-medium transition-all ${
                          isCurrent 
                            ? 'bg-blue-600 text-white shadow-md' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  disabled={offset + limit >= total}
                  onClick={() => searchForms(offset + limit)}
                  className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-sm"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* History Modal */}
      {showModal && selectedForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="sticky top-0 z-10 bg-gradient-to-r from-gray-900 to-gray-800 text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">
                    Application Timeline
                  </h3>
                  <p className="text-gray-300 mt-1 opacity-90">
                    {selectedForm.form_type} • {selectedForm.form_number}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedForm(null);
                    setHistory([]);
                  }}
                  className="text-gray-300 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {historyLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-blue-200 rounded-full"></div>
                    <div className="absolute top-0 left-0 w-16 h-16 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                  </div>
                  <p className="mt-4 text-gray-600 font-medium">Loading timeline...</p>
                </div>
              ) : history.length > 0 ? (
                <div className="space-y-8">
                  {history.map((h, index) => (
                    <div
                      key={h.id || index}
                      className="relative group"
                    >
                      {/* Timeline connector */}
                      {index !== history.length - 1 && (
                        <div className="absolute left-8 top-16 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 to-transparent group-hover:from-blue-400 transition-colors"></div>
                      )}
                      
                      <div className="flex gap-6">
                        {/* Timeline dot */}
                        <div className="relative z-10">
                          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg transform transition-transform group-hover:scale-110
                            ${h.status === 'Approved' ? 'bg-gradient-to-br from-emerald-100 to-emerald-200' :
                              h.status === 'Rejected' ? 'bg-gradient-to-br from-rose-100 to-rose-200' :
                              h.status === 'Under Review' ? 'bg-gradient-to-br from-blue-100 to-blue-200' : 
                              'bg-gradient-to-br from-amber-100 to-amber-200'}`}>
                            <div className={`p-3 rounded-xl
                              ${h.status === 'Approved' ? 'bg-emerald-500' :
                                h.status === 'Rejected' ? 'bg-rose-500' :
                                h.status === 'Under Review' ? 'bg-blue-500' : 
                                'bg-amber-500'}`}>
                              {h.status === 'Approved' ? (
                                <CheckCircle className="w-6 h-6 text-white" />
                              ) : h.status === 'Rejected' ? (
                                <XCircle className="w-6 h-6 text-white" />
                              ) : h.status === 'Under Review' ? (
                                <Loader2 className="w-6 h-6 text-white" />
                              ) : (
                                <Clock className="w-6 h-6 text-white" />
                              )}
                            </div>
                          </div>
                          
                          {/* Date badge */}
                          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-white px-3 py-1 rounded-lg shadow-sm border border-gray-200 text-xs font-medium text-gray-600 whitespace-nowrap">
                            {formatShortDate(h.date || h.created_at)}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 pb-8 pt-2">
                          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            {/* Status Header */}
                            <div className="flex items-start justify-between mb-6">
                              <div>
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="text-xl font-bold text-gray-900 capitalize">
                                    {h.status || "Status Update"}
                                  </h4>
                                  <StatusBadge status={h.status} size="sm" />
                                  {h.doc_status && <DocumentStatusBadge status={h.doc_status} size="sm" />}
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Calendar className="w-4 h-4" />
                                  {formatDate(h.date || h.created_at)}
                                </div>
                              </div>
                              <span className="text-xs font-medium text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                                Entry #{h.id || index + 1}
                              </span>
                            </div>
                            
                            {/* Content Grid */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {/* Remarks Section */}
                              {h.remark && (
                                <div className="bg-white rounded-xl p-5 border border-gray-200 hover:border-blue-200 transition-colors">
                                  <div className="flex items-center gap-3 mb-3">
                                    <div className="p-2 rounded-lg bg-blue-50">
                                      <MessageSquare className="w-5 h-5 text-blue-500" />
                                    </div>
                                    <h5 className="font-semibold text-gray-900">Remarks</h5>
                                  </div>
                                  <p className="text-gray-700 leading-relaxed">{h.remark}</p>
                                </div>
                              )}

                              {/* Document Status Section */}
                              {h.doc_status && (
                                <div className="bg-white rounded-xl p-5 border border-gray-200 hover:border-emerald-200 transition-colors">
                                  <div className="flex items-center gap-3 mb-3">
                                    <div className="p-2 rounded-lg bg-emerald-50">
                                      <FileText className="w-5 h-5 text-emerald-500" />
                                    </div>
                                    <h5 className="font-semibold text-gray-900">Document Status</h5>
                                  </div>
                                  <div className="mb-4">
                                    <DocumentStatusBadge status={h.doc_status} />
                                  </div>
                                  {h.doc_remark && (
                                    <div>
                                      <p className="text-sm font-medium text-gray-600 mb-2">Document Remarks:</p>
                                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{h.doc_remark}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Metadata */}
                            <div className="flex items-center justify-between mt-6 pt-5 border-t border-gray-200">
                              <div className="flex items-center gap-3 text-sm text-gray-500">
                                <div className="flex items-center gap-2">
                                  <div className="p-1 rounded-md bg-gray-100">
                                    <User className="w-4 h-4" />
                                  </div>
                                  <span>System Update</span>
                                </div>
                              </div>
                              <button className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2">
                                <Download className="w-4 h-4" />
                                Download Details
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <Clock className="w-10 h-10 text-gray-400" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-700 mb-2">No Timeline Available</h4>
                  <p className="text-gray-500 max-w-md mx-auto">
                    Application history will appear here as your application progresses through different stages.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ApplicationTrack;